import type {
  CreateStakeholderRequest,
  LogInteractionRequest,
  StakeholderDetail,
  StakeholderStats,
  StakeholderOption,
  StakeholderSummary,
  StakeholderTag,
  UpdateStakeholderRequest,
} from '@todo/shared';
import type { ApiSuccess, PaginationMeta } from '@todo/shared';
import { api, del, get, patch, post } from './client';

/** What POST returns: the contact, plus a duplicate-email warning. */
export interface CreateStakeholderResult {
  stakeholder: StakeholderDetail;
  duplicateOf: { id: string; displayName: string } | null;
}

export interface StakeholderPage {
  rows: StakeholderSummary[];
  meta: PaginationMeta;
}

/** /api/stakeholders - external contacts. Admin + Super Admin only. */
export const stakeholderApi = {
  /** `tags` travels comma-separated - it is a query string, not a body. */
  async list(params: {
    search?: string;
    tags?: StakeholderTag[];
    staleDays?: number;
    page?: number;
    pageSize?: number;
  } = {}): Promise<StakeholderPage> {
    const res = await api.get<ApiSuccess<StakeholderSummary[]>>('/stakeholders', {
      params: {
        ...(params.search ? { search: params.search } : {}),
        ...(params.tags?.length ? { tags: params.tags.join(',') } : {}),
        ...(params.staleDays ? { staleDays: params.staleDays } : {}),
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
      },
    });
    return {
      rows: res.data.data,
      meta: res.data.meta ?? { page: 1, pageSize: 20, total: res.data.data.length, totalPages: 1 },
    };
  },

  /**
   * Rows for the Meetings and CG Sign pickers. Admin-gated like the rest
   * of the module, so a non-admin gets 403 - callers must only ask when
   * the signed-in person is an Admin or Super Admin.
   */
  options(search?: string): Promise<StakeholderOption[]> {
    return get<StakeholderOption[]>('/stakeholders/options', search ? { search } : undefined);
  },

  /** Org-wide counts for the header strip - not the current page's. */
  stats(): Promise<StakeholderStats> {
    return get<StakeholderStats>('/stakeholders/stats');
  },

  byId(id: string): Promise<StakeholderDetail> {
    return get<StakeholderDetail>(`/stakeholders/${id}`);
  },

  create(body: CreateStakeholderRequest): Promise<CreateStakeholderResult> {
    return post<CreateStakeholderResult>('/stakeholders', body);
  },

  update(id: string, body: UpdateStakeholderRequest): Promise<StakeholderDetail> {
    return patch<StakeholderDetail>(`/stakeholders/${id}`, body);
  },

  remove(id: string): Promise<void> {
    return del(`/stakeholders/${id}`);
  },

  // Both interaction calls return the refreshed contact, so the timeline
  // and the last-contacted stamp redraw from one response.

  logInteraction(id: string, body: LogInteractionRequest): Promise<StakeholderDetail> {
    return post<StakeholderDetail>(`/stakeholders/${id}/interactions`, body);
  },

  async removeInteraction(id: string, interactionId: string): Promise<StakeholderDetail> {
    const res = await api.delete<ApiSuccess<StakeholderDetail>>(
      `/stakeholders/${id}/interactions/${interactionId}`,
    );
    return res.data.data;
  },
};
